import React, { lazy, useEffect, useState } from "react";
import "./App.css";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { themeChange } from "theme-change";
import { useDispatch, useSelector } from "react-redux";
import initializeApp from "./app/init";
import { setCredentials, selectCurrentToken } from "./app/authSlice";
import LoadingSpinner from "./components/common/LoadingSpinner";

// Unified Mock Test Components (used for all test types: mock, mini-mock, class-test)
import SingleMockTest from "./features/mocktests/components/SingleMockTest";
import SpeakingSection from "./features/mocktests/components/SpeakingSection";
import ListeningPage from "./features/mocktests/components/ListeningPage";
import ReadingPage from "./features/mocktests/components/ReadingPage";
import WritingPage from "./features/mocktests/components/WritingPage";
import SpeakingPage from "./features/mocktests/components/SpeakingPage";

// Test type constants
import { TEST_TYPES } from "./features/mocktests/mockTestSlice";

// Importing pages
const Layout = lazy(() => import("./containers/Layout"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Register = lazy(() => import("./pages/Register"));
const Documentation = lazy(() => import("./pages/Documentation"));

// Initializing different libraries
initializeApp();

// Silently attempts a refresh on page reload before rendering protected routes.
// Skipped entirely on public routes to avoid unnecessary network calls.
function PersistLogin({ children }) {
  const dispatch = useDispatch();
  const token = useSelector(selectCurrentToken);
  const location = useLocation();
  const [isVerifying, setIsVerifying] = useState(true);

  const PUBLIC_ROUTES = [
    "/login",
    "/forgot-password",
    "/register",
    "/documentation",
  ];
  const isPublicRoute = PUBLIC_ROUTES.some((r) =>
    location.pathname.startsWith(r),
  );

  useEffect(() => {
    // Don't attempt refresh on public pages or if we already have a token
    if (isPublicRoute || token) {
      setIsVerifying(false);
      return;
    }

    const BASE_URL = (
      process.env.REACT_APP_API_BASE_URL || "https://stagingapi.acadimyst.com/"
    ).replace(/\/$/, "");

    const silentRefresh = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/api/v1/en/student/auth/refresh-token`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({}),
          },
        );
        if (response.ok) {
          const data = await response.json();
          const newToken = data?.data?.data?.token;
          const maxSessionUntil = data?.data?.data?.maxSessionUntil;
          if (newToken) {
            dispatch(setCredentials({ token: newToken, maxSessionUntil }));
          }
        }
      } catch (_) {
        // Silent fail — AuthGuard will redirect to /login if needed
      } finally {
        setIsVerifying(false);
      }
    };

    silentRefresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // On public routes skip the spinner entirely
  if (isPublicRoute) {
    return children;
  }

  if (isVerifying) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return children;
}

// Auth Guard Component
function AuthGuard({ children }) {
  const location = useLocation();
  const publicRoutes = [
    "/login",
    "/forgot-password",
    "/register",
    "/documentation",
  ];

  // Check if current route is public
  const isPublicRoute = publicRoutes.includes(location.pathname);

  // Get token from Redux memory
  const token = useSelector(selectCurrentToken);

  // If it's a public route, render the children
  if (isPublicRoute) {
    return children;
  }

  // If not a public route and no token, redirect to login
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // If authenticated, render children
  return children;
}

function App() {
  const token = useSelector(selectCurrentToken);

  useEffect(() => {
    // 👆 daisy UI themes initialization
    themeChange(false);

    // Remove legacy cross-domain tokens from the URL. The following reload lets
    // PersistLogin restore authentication using the refresh-token cookie.
    const { extractTokenFromUrl } = require("./utils/crossDomainAuth");
    const tokenFound = extractTokenFromUrl();

    if (tokenFound) {
      // Force a refresh to apply the new token and trigger AuthGuard
      window.location.href = "/app/dashboard";
    }
  }, []);

  return (
    <>
      <Router>
        <PersistLogin>
          <AuthGuard>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/register" element={<Register />} />
              <Route path="/documentation" element={<Documentation />} />

              {/* Place new routes over this */}
              <Route path="/app/*" element={<Layout />} />

              <Route
                path="*"
                element={
                  <Navigate to={token ? "/app/dashboard" : "/login"} replace />
                }
              />

              <Route
                path="/mock-test/:testId"
                element={<SingleMockTest testType={TEST_TYPES.MOCK_TEST} />}
              />

              <Route
                path="/mock-speaking/:testId"
                element={<SpeakingSection />}
              />

              <Route
                path="/mock-test/:testId"
                element={<SingleMockTest testType={TEST_TYPES.MOCK_TEST} />}
              />
              <Route
                path="/mock-test/:testId/listening"
                element={<ListeningPage />}
              />
              <Route
                path="/mock-test/:testId/reading"
                element={<ReadingPage />}
              />
              <Route
                path="/mock-test/:testId/writing"
                element={<WritingPage />}
              />
              <Route
                path="/mock-test/:testId/speaking"
                element={<SpeakingPage />}
              />

              {/* Bundle Test Routes */}
              <Route
                path="/bundle-test/:testId/listening"
                element={<ListeningPage isBundle={true} />}
              />
              <Route
                path="/bundle-test/:testId/reading"
                element={<ReadingPage isBundle={true} />}
              />
              <Route
                path="/bundle-test/:testId/writing"
                element={<WritingPage isBundle={true} />}
              />
              <Route
                path="/bundle-test/:testId/speaking"
                element={<SpeakingPage isBundle={true} />}
              />

              {/* Mini Mock Test Routes - Uses unified components */}
              <Route
                path="/mini-mock-test/:testId"
                element={<SingleMockTest testType={TEST_TYPES.MINI_MOCK} />}
              />
              <Route
                path="/mini-mock-test/:testId/listening"
                element={<ListeningPage />}
              />
              <Route
                path="/mini-mock-test/:testId/reading"
                element={<ReadingPage />}
              />
              <Route
                path="/mini-mock-test/:testId/writing"
                element={<WritingPage />}
              />
              <Route
                path="/mini-mock-test/:testId/speaking"
                element={<SpeakingPage />}
              />

              {/* Class Test Routes - Uses unified components */}
              <Route
                path="/class-test/:testId"
                element={<SingleMockTest testType={TEST_TYPES.CLASS_TEST} />}
              />
              <Route
                path="/class-test/:testId/listening"
                element={<ListeningPage />}
              />
              <Route
                path="/class-test/:testId/reading"
                element={<ReadingPage />}
              />
              <Route
                path="/class-test/:testId/writing"
                element={<WritingPage />}
              />
              <Route
                path="/class-test/:testId/speaking"
                element={<SpeakingPage />}
              />

              {/* Bundle Test Routes - reuse mock test components with bundle mode */}
              <Route
                path="/bundle-test/:testId/listening"
                element={<ListeningPage isBundle={true} />}
              />
              <Route
                path="/bundle-test/:testId/reading"
                element={<ReadingPage isBundle={true} />}
              />
              <Route
                path="/bundle-test/:testId/writing"
                element={<WritingPage isBundle={true} />}
              />
              <Route
                path="/bundle-test/:testId/speaking"
                element={<SpeakingPage isBundle={true} />}
              />
            </Routes>
          </AuthGuard>
        </PersistLogin>
      </Router>
    </>
  );
}

export default App;
